import { Form, Field, FieldProps } from "formik"
import { ChangeEvent, FC, useState } from "react"
import { InputField } from "./InputField"
import { InputRangeWithField } from "./InputRangeWithField"
import { isNaN } from "lodash"
import { Sections } from "../../defines/Sections"
import { DashboardSection } from "./DasboardSection"
export type DashboardFormProps = {
  /**
   * Existing response data used to display monthly payment
   */
  responseData?: any
  /**
   * Form errors
   */
  errors: any
  /**
   * Field of values which indicates if a field was interacted with
   */
  touched: any
  /**
   * Form values
   */
  values?: any
  /**
   * Form handle change function
   */
  handleChange: (
    event:
      | ChangeEvent<HTMLInputElement>
      | { target: { name: string; value: { id: number; value: string } } }
  ) => void
  /**
   * Field handle set field value
   */
  setFieldValue: any
  /**
   * Sets all fields to readonly
   */
  readOnly?: boolean
  /**
   * Function used to switch between tabs
   */
  setTabIndex?: (value: number) => void
}

const validateNumber = (n: any) => {
  let error

  if (isNaN(parseFloat(n)) && !isNaN(n - 0)) {
    error = "Please enter a number."
  }

  return error
}

const validatePercentage = (n: any, values: any) => {
  let error

  const cleanValue = String(n).replaceAll("%", "")

  if (isNaN(parseFloat(cleanValue)) && !isNaN(Number(cleanValue) - 0)) {
    error = "Please enter a number."
  }

  if (Number(cleanValue) > Number(values.Coupon)) {
    error = "Spread value has to be smaller than Coupon value."
  }

  return error
}

export const DashboardForm: FC<DashboardFormProps> = ({
  responseData = null,
  errors,
  touched,
  values = {},
  handleChange,
  setFieldValue,
  readOnly = false,
  setTabIndex = () => {},
}) => {
  const [selectedSection, setSelectedSection] = useState<any>()
  return (
    <Form className="flex flex-col items-center justify-center pb-24 space-y-3">
      {!readOnly && (
        <div className="mb-4 mx-auto w-full max-w-sm text-center">
          <p className="text-sm">
            <b>Enter your details below:</b>
          </p>
        </div>
      )}
      <DashboardSection
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        sectionTitle="INCOME & SAVINGS"
        section={Sections.IncomeAndSavings}
      >
        <Field name="Gross_Income" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                prefix="$"
                id="Gross_Income"
                label="Gross Annual Income (before tax)"
                type="currency"
                value={field.value}
                setCurrencyValue={(values) => {
                  setFieldValue("Gross_Income", values.value)
                }}
                error={errors["Gross_Income"]}
                touched={touched["Gross_Income"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
        <Field name="DownPayment" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                prefix="$"
                id="DownPayment"
                label="Savings"
                type="currency"
                value={isNaN(field.value) ? 0 : field.value}
                setCurrencyValue={(values) => {
                  setFieldValue("DownPayment", values.value)
                }}
                error={errors["DownPayment"]}
                touched={touched["DownPayment"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
      </DashboardSection>
      <DashboardSection
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        sectionTitle="MONTHLY DEBT REPAYMENTS"
        section={Sections.MonthlyDebtRepayments}
      >
        <Field name="MonthlyCar" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                prefix="$"
                id="MonthlyCar"
                label="Group Car Loan"
                type="currency"
                value={field.value}
                setCurrencyValue={(values) => {
                  setFieldValue("MonthlyCar", values.value)
                }}
                error={errors["MonthlyCar"]}
                touched={touched["MonthlyCar"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
        <Field name="MonthlyCC" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                prefix="$"
                id="MonthlyCC"
                label="Credit Card Minimums"
                type="currency"
                value={field.value}
                setCurrencyValue={(values) => {
                  setFieldValue("MonthlyCC", values.value)
                }}
                error={errors["MonthlyCC"]}
                touched={touched["MonthlyCC"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
        <Field name="MonthlyStuLoan" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                prefix="$"
                id="MonthlyStuLoan"
                label="Student Loans"
                type="currency"
                value={field.value}
                setCurrencyValue={(values) => {
                  setFieldValue("MonthlyStuLoan", values.value)
                }}
                error={errors["MonthlyStuLoan"]}
                touched={touched["MonthlyStuLoan"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
      </DashboardSection>
      <DashboardSection
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        sectionTitle="EXPECTED MONTHLY HOUSING EXPENSES"
        section={Sections.ExpectedMonthlyHousingExpenses}
      >
        <Field name="PptyTax" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                prefix="$"
                id="PptyTax"
                label="Property Tax (Monthly)"
                type="currency"
                value={field.value}
                setCurrencyValue={(values) => {
                  setFieldValue("PptyTax", values.value)
                }}
                error={errors["PptyTax"]}
                touched={touched["PptyTax"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
        <Field name="MonthlyHomeIns" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                prefix="$"
                id="MonthlyHomeIns"
                label="Home Owner Insurance (Monthly)"
                type="currency"
                value={field.value}
                setCurrencyValue={(values) => {
                  setFieldValue("MonthlyHomeIns", values.value)
                }}
                error={errors["MonthlyHomeIns"]}
                touched={touched["MonthlyHomeIns"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
        <Field name="MonthlyOtherExp" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                prefix="$"
                id="MonthlyOtherExp"
                label="Utilities"
                type="currency"
                value={field.value}
                setCurrencyValue={(values) => {
                  setFieldValue("MonthlyOtherExp", values.value)
                }}
                error={errors["MonthlyOtherExp"]}
                touched={touched["MonthlyOtherExp"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
      </DashboardSection>

      <DashboardSection
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        sectionTitle="CLOSING COSTS"
        section={Sections.ClosingCosts}
      >
        <Field name="VarClosingCosts" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                id="VarClosingCosts"
                label="Variable Closing Costs (based on price)"
                type="number"
                value={field.value}
                handleChange={handleChange}
                error={errors["VarClosingCosts"]}
                touched={touched["VarClosingCosts"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
        <Field name="FixedClosingCosts" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputField
                prefix="$"
                id="FixedClosingCosts"
                label="Fixed Closing Costs"
                type="currency"
                value={field.value}
                setCurrencyValue={(values) => {
                  setFieldValue("FixedClosingCosts", values.value)
                }}
                error={errors["FixedClosingCosts"]}
                touched={touched["FixedClosingCosts"]}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
        <Field name="MinDownPaymentPct" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputRangeWithField
                id="MinDownPaymentPct"
                label="Minimum Down Payment"
                value={field.value}
                handleChange={handleChange}
                error={errors["MinDownPaymentPct"]}
                touched={touched["MinDownPaymentPct"]}
                min={0}
                max={1}
                step={0.01}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
      </DashboardSection>

      <DashboardSection
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        sectionTitle="FINANCING"
        section={Sections.Financing}
      >
        <Field name="TermOfMortgageYrs" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputRangeWithField
                id="TermOfMortgageYrs"
                label="Term of Mortgage"
                value={field.value}
                handleChange={handleChange}
                error={errors["TermOfMortgageYrs"]}
                touched={touched["TermOfMortgageYrs"]}
                min={5}
                max={30}
                step={5}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
        <Field name="AnnInterestRate" validate={validateNumber}>
          {({ field }: FieldProps) => (
            <div className="mb-4 mx-auto w-full max-w-sm">
              <InputRangeWithField
                id="AnnInterestRate"
                label="Annual Interest Rate"
                value={field.value}
                handleChange={handleChange}
                error={errors["AnnInterestRate"]}
                touched={touched["AnnInterestRate"]}
                min={0}
                max={0.2}
                step={0.01}
                readOnly={readOnly}
              />
            </div>
          )}
        </Field>
      </DashboardSection>
    </Form>
  )
}
